import { z } from 'zod'

import { emojiIdToNative } from './emojiSearch'

const EMOJI_FREQUENCY_KEY = 'buffer_emoji_frequency'
const EMOJI_LAST_USED_KEY = 'buffer_emoji_last_used'
const LEGACY_EMOJI_MART_KEY = 'emoji-mart.frequently'

const EmojiFrequencyMapSchema = z.record(
  z.string(),
  z.number().int().positive(),
)

type EmojiFrequencyMap = z.infer<typeof EmojiFrequencyMapSchema>

/**
 * Where one surface keeps its emoji use counts. Surfaces that keep separate
 * stores do not reorder each other's "frequently used" emoji.
 */
export type EmojiFrequencyStore = {
  frequencyKey: string
  lastUsedKey: string
}

/** The Composer's store, shared by every surface that inserts emoji into text. */
export const COMPOSER_EMOJI_FREQUENCY_STORE: EmojiFrequencyStore = {
  frequencyKey: EMOJI_FREQUENCY_KEY,
  lastUsedKey: EMOJI_LAST_USED_KEY,
}

/**
 * Checks if a string is an emoji ID (like "kissing_heart") rather than an actual emoji character
 */
const isEmojiId = (str: string): boolean => {
  // Emoji characters typically have unicode values > 127 (non-ASCII)
  // Emoji IDs are ASCII strings like "kissing_heart", "thumbs_up", etc.
  return /^[a-z0-9_+-]+$/i.test(str)
}

/**
 * Migrates emoji frequency data from the legacy emoji-mart storage format.
 * The legacy format stores emoji IDs (like "kissing_heart") as keys,
 * but our current format expects actual emoji characters (like "😘").
 *
 * This function converts emoji IDs to native emoji characters using Emojibase search data during migration.
 * Uses Zod to validate the data structure before migration.
 *
 * @returns The migrated frequency map, or null if no legacy data exists or validation fails
 */
const migrateLegacyEmojiFrequency =
  async (): Promise<EmojiFrequencyMap | null> => {
    try {
      const legacyData = localStorage.getItem(LEGACY_EMOJI_MART_KEY)
      if (!legacyData) return null

      const parsed: unknown = JSON.parse(legacyData)
      const result = EmojiFrequencyMapSchema.safeParse(parsed)

      if (!result.success) return null

      const legacyMap = result.data
      const migratedMap: EmojiFrequencyMap = {}
      const migratedEntries = await Promise.all(
        Object.entries(legacyMap).map(
          async ([key, count]): Promise<[string, number] | null> => {
            if (!isEmojiId(key)) return [key, count]

            const nativeEmoji = await emojiIdToNative(key)
            return nativeEmoji ? [nativeEmoji, count] : null
          },
        ),
      )

      for (const entry of migratedEntries) {
        if (entry) migratedMap[entry[0]] = entry[1]
      }

      // Only save and remove legacy data if we successfully converted at least some emojis
      if (Object.keys(migratedMap).length > 0) {
        localStorage.setItem(EMOJI_FREQUENCY_KEY, JSON.stringify(migratedMap))
        localStorage.removeItem(LEGACY_EMOJI_MART_KEY)
        return migratedMap
      }

      return null
    } catch {
      return null
    }
  }

/**
 * Heals mixed state where both shortcodes and emoji characters exist in storage.
 * Converts all shortcodes to native emojis and merges duplicate counts.
 */
const healMixedEmojiState = async (
  data: EmojiFrequencyMap,
): Promise<EmojiFrequencyMap | null> => {
  try {
    const healedMap: EmojiFrequencyMap = {}
    const entries = Object.entries(data)
    const hasShortcodes = entries.some(([key]) => isEmojiId(key))
    const healedEntries = await Promise.all(
      entries.map(async ([key, count]): Promise<[string, number] | null> => {
        if (!isEmojiId(key)) return [key, count]

        const nativeEmoji = await emojiIdToNative(key)
        return nativeEmoji ? [nativeEmoji, count] : null
      }),
    )

    for (const entry of healedEntries) {
      if (!entry) continue
      healedMap[entry[0]] = (healedMap[entry[0]] || 0) + entry[1]
    }

    return hasShortcodes ? healedMap : null
  } catch {
    return null
  }
}

// Track if migration is in progress to avoid multiple simultaneous migrations
let migrationPromise: Promise<EmojiFrequencyMap | null> | null = null

/**
 * Triggers async migration of legacy emoji data and healing of mixed state.
 * This should be called when the app loads or when the emoji picker is first used.
 * Only the Composer's store has legacy data to migrate.
 */
export const initializeEmojiFrequency = async (): Promise<void> => {
  if (migrationPromise) {
    await migrationPromise
    return
  }

  migrationPromise = (async (): Promise<EmojiFrequencyMap | null> => {
    try {
      const legacyData = localStorage.getItem(LEGACY_EMOJI_MART_KEY)
      if (legacyData && !localStorage.getItem(EMOJI_FREQUENCY_KEY)) {
        return await migrateLegacyEmojiFrequency()
      }

      const currentData = localStorage.getItem(EMOJI_FREQUENCY_KEY)
      if (currentData) {
        const parsed: unknown = JSON.parse(currentData)
        const result = EmojiFrequencyMapSchema.safeParse(parsed)

        if (result.success) {
          const healedData = await healMixedEmojiState(result.data)
          if (healedData && Object.keys(healedData).length > 0) {
            localStorage.setItem(
              EMOJI_FREQUENCY_KEY,
              JSON.stringify(healedData),
            )
            return healedData
          }
        }
      }
    } catch {
      return null
    }

    return null
  })()

  try {
    await migrationPromise
  } finally {
    migrationPromise = null
  }
}

export const getEmojiFrequency = (
  store: EmojiFrequencyStore = COMPOSER_EMOJI_FREQUENCY_STORE,
): EmojiFrequencyMap => {
  try {
    const stored = localStorage.getItem(store.frequencyKey)

    if (stored) {
      const result = EmojiFrequencyMapSchema.safeParse(JSON.parse(stored))
      return result.success ? result.data : {}
    }

    // If legacy data exists but hasn't been migrated yet, return empty
    // The migration will happen async via initializeEmojiFrequency()
    return {}
  } catch {
    return {}
  }
}

const getLastUsedEmoji = (store: EmojiFrequencyStore): string | null => {
  try {
    return localStorage.getItem(store.lastUsedKey)
  } catch {
    return null
  }
}

export const updateEmojiFrequency = (
  emoji: string,
  store: EmojiFrequencyStore = COMPOSER_EMOJI_FREQUENCY_STORE,
): void => {
  try {
    const frequency = getEmojiFrequency(store)
    frequency[emoji] = (frequency[emoji] || 0) + 1
    localStorage.setItem(store.frequencyKey, JSON.stringify(frequency))

    // Track the last used emoji separately
    localStorage.setItem(store.lastUsedKey, emoji)
  } catch {
    // Silently fail if localStorage is not available
  }
}

export const getFrequentlyUsedEmojis = (
  limit: number,
  store: EmojiFrequencyStore = COMPOSER_EMOJI_FREQUENCY_STORE,
): string[] => {
  const frequency = getEmojiFrequency(store)
  const lastUsed = getLastUsedEmoji(store)
  const frequencyBuckets = new Map<number, string[]>()

  Object.entries(frequency).forEach(([emoji, count]) => {
    if (!frequencyBuckets.has(count)) {
      frequencyBuckets.set(count, [])
    }
    frequencyBuckets.get(count)?.push(emoji)
  })

  // Sort each bucket alphabetically, but put last used emoji at the start of its bucket
  const sortedBuckets: string[] = []
  const sortedCounts = Array.from(frequencyBuckets.keys()).toSorted(
    (a, b) => b - a,
  )

  for (const count of sortedCounts) {
    const emojisInBucket = frequencyBuckets.get(count)
    if (!emojisInBucket) continue

    if (lastUsed && emojisInBucket.includes(lastUsed)) {
      // Show the most recently used emoji first, then sort others alphabetically
      const others = emojisInBucket
        .filter((emoji) => emoji !== lastUsed)
        .toSorted((a, b) => a.localeCompare(b))
      sortedBuckets.push(lastUsed, ...others)
    } else {
      // Sort all alphabetically
      sortedBuckets.push(
        ...emojisInBucket.toSorted((a, b) => a.localeCompare(b)),
      )
    }
  }

  let result = sortedBuckets.slice(0, limit)

  // If last used emoji is not in the result, prepend it and drop the
  // least-frequent entry so the top-frequency emojis are preserved
  if (lastUsed && !result.includes(lastUsed)) {
    result = [lastUsed, ...result.slice(0, limit - 1)]
  }

  return result
}
